import React, { useRef } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { FormSection, SingleSelectPicker, DateTimePickerButton } from '@/components';
import { ITaskFormValues, TASK_STATUS_CONFIG, TASK_PRIORITY, TaskPriorityEnum } from '@/types/admin/tasks';
import { formatDateOnly } from '@/utils/admin/date';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';
import { AnchorRect } from '@/hooks/common/useAnchoredPosition';
import { useResponsive } from '@/hooks/common/useResponsive';
import { DesktopDropdownSelector } from '@/components/common/admin/desktop/DesktopDropdownSelector';

interface TaskSettingsSectionProps {
  formData: ITaskFormValues;
  errors: Record<string, string | undefined>;
  onFieldChange: (field: keyof ITaskFormValues, value: any) => void;
  onDueDatePress: (anchor?: AnchorRect) => void;
  onClearDueDate: () => void;
}

export const TaskSettingsSection: React.FC<TaskSettingsSectionProps> = ({
  formData,
  errors,
  onFieldChange,
  onDueDatePress,
  onClearDueDate,
}) => {
  const { layoutMode } = useResponsive();
  const statusOptions = Object.values(TASK_STATUS_CONFIG).map(status => ({
    label: status.label,
    value: status.value,
  }));

  const priorityOptions = TASK_PRIORITY.map(priority => ({
    label: priority.label,
    value: priority.value.toString(),
  }));

  const formatDueDate = (date: Date | string | null) => (date ? formatDateOnly(date) : null);

  const dueDateBtnRef = useRef<View | null>(null);
  const dueDateButtonTestId = 'task-due-date-btn';

  const handlePressDueDate = () => {
    if (Platform.OS === 'web') {
      try {
        const el: any = (globalThis as any)?.document?.querySelector?.(`[data-testid="${dueDateButtonTestId}"]`);
        if (el && el.getBoundingClientRect) {
          const r = el.getBoundingClientRect();
          const anchor: AnchorRect = { x: r.left, y: r.top, w: r.width, h: r.height };
          onDueDatePress(anchor);
          return;
        }
      } catch {}
    }
    onDueDatePress();
  };

  return (
    <FormSection title="Task Settings" subtitle="Status, priority, and scheduling">
      <View style={styles.row}>
        <View style={[styles.fieldContainer, styles.halfWidth]}>
          {layoutMode === 'desktop' ? (
            <DesktopDropdownSelector
              label="Status"
              selectedValue={formData.taskStatus}
              options={statusOptions}
              onChange={(value) => onFieldChange('taskStatus', value)}
              placeholder="Select status"
              error={errors.taskStatus}
              required
            />
          ) : (
            <SingleSelectPicker
              label="Status"
              selectedValue={formData.taskStatus}
              onSelectionChange={(value: string) => onFieldChange('taskStatus', value)}
              options={statusOptions}
              placeholder="Select status"
              error={errors.taskStatus}
              required
            />
          )}
        </View>

        <View style={[styles.fieldContainer, styles.halfWidth]}>
          {layoutMode === 'desktop' ? (
            <DesktopDropdownSelector
              label="Priority"
              selectedValue={formData.priority?.toString()}
              options={priorityOptions}
              onChange={(value) => onFieldChange('priority', parseInt(value) as TaskPriorityEnum)}
              placeholder="Select priority"
              error={errors.priority}
              required
            />
          ) : (
            <SingleSelectPicker
              label="Priority"
              selectedValue={formData.priority?.toString()}
              onSelectionChange={(value: string) => onFieldChange('priority', parseInt(value) as TaskPriorityEnum)}
              options={priorityOptions}
              placeholder="Select priority"
              error={errors.priority}
              required
            />
          )}
        </View>
      </View>

      <View style={styles.fieldContainer}>
        <Text style={styles.label}>Due Date</Text>
        <View style={styles.dueDateContainer}>
          <TouchableOpacity
            style={[styles.dueDateButton, styles.datePickerButton]}
            onPress={handlePressDueDate}
            ref={dueDateBtnRef as any}
            testID={dueDateButtonTestId}
          >
            <Text style={[styles.datePickerText, !formData.dueDate && styles.placeholderText]}>
              {formData.dueDate ? formatDueDate(formData.dueDate) || 'Select due date' : 'Select due date'}
            </Text>
            <SVG_COMPONENTS.CALENDAR width={16} height={16} color={ADMIN_COLORS.TEXT_SECONDARY} />
          </TouchableOpacity>
          {formData.dueDate && (
            <TouchableOpacity
              style={styles.clearButton}
              onPress={onClearDueDate}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <SVG_COMPONENTS.X_CIRCLE width={20} height={20} color={ADMIN_COLORS.TEXT_SECONDARY} />
            </TouchableOpacity>
          )}
        </View>
        {errors.dueDate && <Text style={styles.errorText}>{errors.dueDate}</Text>}
      </View>

      <View style={styles.fieldContainer}>
        <View style={styles.checkboxContainer}>
          <TouchableOpacity
            style={styles.checkbox}
            onPress={() => onFieldChange('isPrivate', !formData.isPrivate)}
          >
            {formData.isPrivate && (
              <SVG_COMPONENTS.CHECK_CIRCLE width={16} height={16} color={ADMIN_COLORS.PRIMARY} />
            )}
          </TouchableOpacity>
          <Text style={styles.checkboxLabel}>Private Task</Text>
        </View>
        <Text style={styles.checkboxDescription}>
          Private tasks are only visible to assigned users and administrators
        </Text>
      </View>

      <View style={styles.fieldContainer}>
        <View style={styles.checkboxContainer}>
          <TouchableOpacity
            style={styles.checkbox}
            onPress={() => onFieldChange('isBlocked', !formData.isBlocked)}
          >
            {formData.isBlocked && (
              <SVG_COMPONENTS.CHECK_CIRCLE width={16} height={16} color={ADMIN_COLORS.PRIMARY} />
            )}
          </TouchableOpacity>
          <Text style={styles.checkboxLabel}>Blocked Task</Text>
        </View>
        <Text style={styles.checkboxDescription}>
          Blocked tasks require attention before they can proceed
        </Text>
      </View>
    </FormSection>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    gap: 12,
  },
  fieldContainer: {
    marginBottom: 16,
  },
  halfWidth: {
    flex: 1,
  },
  label: {
    fontSize: 16,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 8,
  },
  dueDateContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  dueDateButton: {
    flex: 1,
  },
  datePickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    minHeight: 48,
  },
  datePickerText: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_PRIMARY,
    flex: 1,
  },
  placeholderText: {
    color: ADMIN_COLORS.TEXT_TERTIARY,
  },
  clearButton: {
    padding: 8,
    borderRadius: 6,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
  },
  errorText: {
    fontSize: 14,
    color: ADMIN_COLORS.ERROR,
    marginTop: 4,
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 4,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: ADMIN_COLORS.BORDER,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  checkboxDescription: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginLeft: 32,
    lineHeight: 20,
  },
});
